// types
import type { CalendarEvent } from '@/types/components/calendar'
import type { AppointmentListItem } from '@/types/domain/appointment'

/**
 * Theme tokens used to colour calendar chips and the shared hover popover.
 */
export interface AppointmentStatusTheme {
  name: string
  statusColor: string
  textColor: string
  outcomeColor: string
}

/**
 * Fields the calendar needs for filtering, rendering, and the hover popover.
 * Kept intentionally small so the calendar never holds the full appointment payload.
 */
export interface CalendarAppointmentExtendedProps {
  status: string
  method: string
  time: string
  customerName: string
  assignedToName: string | null
  dealershipName: string
  customerConfirmed: boolean
  note: string | null
  outcome: string | null
  outcomeReason: string | null
  dealershipId: number
  createdById: number | undefined
  statusColor: string
}

/**
 * Calendar event carrying appointment fields in `meta` for filtering and the hover popover.
 */
export type CalendarAppointmentEvent = CalendarEvent & {
  meta: CalendarAppointmentExtendedProps
}

/**
 * Active calendar sidebar filters.
 */
export interface CalendarEventFilters {
  statuses: string[]
  dealershipIds: number[]
  createdByIds: number[]
}

/**
 * Dealership fields required by the calendar filter dropdown.
 */
export interface DealershipFilterItem {
  id: number
  displayName: string
  groupName?: string | null
}

/**
 * Dealership options grouped for the filter select.
 */
export interface DealershipFilterGroup {
  groupName: string
  children: DealershipFilterItem[]
}

/**
 * Colour tokens keyed by appointment status.
 */
export const APPOINTMENT_STATUS_THEMES: AppointmentStatusTheme[] = [
  {
    name: 'unassigned',
    statusColor: 'utility-gray-600',
    textColor: 'gray-600',
    outcomeColor: 'gray-800'
  },
  {
    name: 'assigned',
    statusColor: 'utility-blue-900',
    textColor: 'blue-600',
    outcomeColor: 'blue-800'
  },
  {
    name: 'cancelled',
    statusColor: 'utility-error-600',
    textColor: 'red-600',
    outcomeColor: 'red-800'
  },
  {
    name: 'no-show',
    statusColor: 'utility-error-600',
    textColor: 'red-600',
    outcomeColor: 'red-800'
  },
  {
    name: 'completed',
    statusColor: 'utility-success-600',
    textColor: 'green-700',
    outcomeColor: 'green-800'
  },
  {
    name: 'ongoing',
    statusColor: 'utility-purple-600',
    textColor: 'purple-600',
    outcomeColor: 'purple-900'
  }
]

/**
 * Fallback theme used when an appointment status is unknown.
 */
const DEFAULT_APPOINTMENT_STATUS_THEME = APPOINTMENT_STATUS_THEMES[0]

/**
 * Returns the colour theme for an appointment status.
 * @param status - Appointment status key
 */
export function getAppointmentStatusTheme(status: string): AppointmentStatusTheme {
  return APPOINTMENT_STATUS_THEMES.find(theme => theme.name === status) ?? DEFAULT_APPOINTMENT_STATUS_THEME
}

/**
 * Maps list-endpoint appointments into calendar events.
 * @param appointments - Appointments returned by `/v1/appointments`
 */
export function mapAppointmentsToCalendarEvents(appointments: AppointmentListItem[]): CalendarAppointmentEvent[] {
  return appointments.map(appointment => {
    const theme = getAppointmentStatusTheme(appointment.status)

    return {
      id: String(appointment.id),
      title: appointment.customerName,
      description: appointment.note ?? undefined,
      start: appointment.time,
      color: theme.statusColor,
      url: `/agreement/${appointment.agreementId}/appointments/${appointment.id}`,
      className: ['style-appointment', appointment.status],
      meta: {
        status: appointment.status,
        method: appointment.method,
        time: appointment.time,
        customerName: appointment.customerName,
        assignedToName: appointment.assignedToName ?? null,
        dealershipName: appointment.dealershipName,
        customerConfirmed: appointment.customerConfirmed,
        note: appointment.note,
        outcome: appointment.outcome,
        outcomeReason: appointment.outcomeReason,
        dealershipId: appointment.dealershipId,
        createdById: appointment.createdBy?.id,
        statusColor: theme.statusColor
      }
    }
  })
}

/**
 * Filters calendar events by status, dealership, and creator.
 * Empty dealership/creator selections mean "all".
 * @param events - Mapped calendar events
 * @param filters - Active sidebar filters
 */
export function filterCalendarEvents(
  events: CalendarAppointmentEvent[],
  filters: CalendarEventFilters
): CalendarAppointmentEvent[] {
  return events.filter(event => {
    // Drop events whose status checkbox is unchecked
    if (!filters.statuses.includes(event.meta.status)) {
      return false
    }

    // An empty dealership selection means every dealership is included
    if (filters.dealershipIds.length > 0 && !filters.dealershipIds.includes(event.meta.dealershipId)) {
      return false
    }

    // An empty creator selection means every booker is included
    if (
      filters.createdByIds.length > 0 &&
      (event.meta.createdById === undefined || !filters.createdByIds.includes(event.meta.createdById))
    ) {
      return false
    }

    return true
  })
}

/**
 * Groups dealerships by group name for the calendar filter select.
 * @param dealerships - Dealerships the user can access
 */
export function groupDealershipsByGroup(dealerships: DealershipFilterItem[]): DealershipFilterGroup[] {
  // Guard against a missing or malformed payload from the dealerships endpoint
  if (!Array.isArray(dealerships) || dealerships.length === 0) {
    return []
  }

  const grouped = new Map<string, DealershipFilterItem[]>()

  dealerships.forEach(dealership => {
    const groupName = dealership.groupName || 'Other'
    const children = grouped.get(groupName)

    // Create the group bucket on first encounter
    if (!children) {
      grouped.set(groupName, [dealership])
      return
    }

    children.push(dealership)
  })

  return Array.from(grouped.entries())
    .sort(([leftGroupName], [rightGroupName]) => leftGroupName.localeCompare(rightGroupName))
    .map(([groupName, children]) => ({
      groupName,
      children
    }))
}
